import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";

/** Row style: `default` rows (divided when `divided`), or `card`: each row a
 *  bordered, rounded box with a gap between rows and no dividers. */
export type ListVariant = "default" | "card";
/** Row spacing: `comfortable` (py-2.5) or `compact` (py-1.5). */
export type ListDensity = "comfortable" | "compact";
/** The list element. `ol` changes the semantics only: a ListItem is a flex
 *  row, so it draws no marker, and any visible number goes in `leading`. */
export type ListElement = "ul" | "ol";

export interface ListProps extends React.HTMLAttributes<HTMLUListElement> {
  /** Hairline separators between items. `variant="card"` never draws them. */
  divided?: boolean;
  /** Default `default`. */
  variant?: ListVariant | undefined;
  /** Default `comfortable`. */
  density?: ListDensity | undefined;
  /** Default `ul`. */
  as?: ListElement | undefined;
}

/* Row padding lives on the list's [&>li] selector, so raw <li> children and
   ListItems get the same rows and density is decided once per list. */
const DENSITY: Record<ListDensity, string> = {
  comfortable: "[&>li]:py-2.5",
  compact: "[&>li]:py-1.5",
};

/* The card row box, through the same selector. The corner is the 8px
   `rounded` word of lib/shape.ts (a literal here so Tailwind sees the
   variant-prefixed class). No row fill, so a ListItem's teal selection fill
   still shows. */
const CARD =
  "flex flex-col gap-2 [&>li]:border [&>li]:border-pb-border [&>li]:rounded-md [&>li]:px-3";

/* Generic list container (guide §10 "List", register #12). Data-blind:
   callers compose ListItem with whatever leading/trailing content they need.
   Semantics stay native (<ul>/<ol>/<li> → list/listitem roles). */
export const List = forwardRef<HTMLUListElement, ListProps>(function List(
  {
    divided = true,
    variant = "default",
    density = "comfortable",
    as = "ul",
    className,
    children,
    ...rest
  },
  ref,
) {
  const props = {
    className: cn(
      "w-full",
      variant === "card" ? CARD : divided && "divide-y divide-pb-border",
      DENSITY[density],
      className,
    ),
    ...rest,
  };
  // An <ol> has every property of a <ul>, so the ref's type still holds.
  return as === "ol" ? (
    <ol ref={ref as React.Ref<HTMLOListElement>} {...props}>
      {children}
    </ol>
  ) : (
    <ul ref={ref} {...props}>
      {children}
    </ul>
  );
});
